import {
    Avatar,
    Colors,
    Icon,
    Pills,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';

import type { OverflowNodeProps } from '../../types';

import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { useAmelioTranslations } from '@/hooks/utils/useAmelioTranslations';
import { getInitials, getProfilePictureUrl } from '@/utils/user';

export const OverflowNode = ({
    position,
    hiddenNodes = [],
    count = 0,
    onRemove,
}: OverflowNodeProps) => {
    const { t } = useAmelioTranslations();
    const { getJobTitle } = useCompanyInformation();

    return (
        <TooltipProvider>
            <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                    <div
                        data-testid={'overflow-node-trigger'}
                        className={
                            'absolute -translate-x-1/2 -translate-y-1/2 transform cursor-pointer bg-transparent transition-all duration-500 ease-out'
                        }
                        style={{
                            left: `${position.x}%`,
                            top: `${position.y}%`,
                            zIndex: 5,
                        }}
                    >
                        <div className={'flex flex-col items-center gap-1 sm:gap-1.5'}>
                            {/* Avatar Circle with Users Icon */}
                            <div
                                className={
                                    'text-primary bg-primary-50 flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-linear-to-br shadow-lg sm:h-20 sm:w-20'
                                }
                            >
                                <Icon name={'users-profile-2user'} />
                            </div>

                            <Pills
                                variant={'primary'}
                                size={'sm'}
                                className={'-mt-4'}
                            >
                                {t('assignReviewers.moreReviewersPill', { count })}
                            </Pills>
                        </div>
                    </div>
                </TooltipTrigger>

                <TooltipContent
                    className={'pointer-events-auto max-w-xs'}
                    side={'top'}
                    layout={'list'}
                >
                    <div className={'space-y-2 px-2 py-1'}>
                        <p
                            className={'mb-2 px-2 pt-2 text-base'}
                            data-testid={'overflow-node-tooltip-header'}
                        >
                            {t('assignReviewers.hiddenTeamMembers')}:
                        </p>

                        <div className={'w-full space-y-1.5'}>
                            {hiddenNodes.map((node) => (
                                <div
                                    key={node.data?.id}
                                    className={
                                        'group hover:bg-base-50 flex items-center gap-2 rounded-sm px-2 py-1 transition-all duration-500 ease-out'
                                    }
                                >
                                    <Avatar
                                        alt={node.data?.firstName || ''}
                                        initials={node.data ? getInitials(node.data) : ''}
                                        src={getProfilePictureUrl(
                                            node.data?.profilePictureUrl || '',
                                        )}
                                        size={'4xs'}
                                        className={'shrink-0'}
                                    />

                                    <span className={'text-base-500 flex-1 text-sm'}>
                                        {getJobTitle(node.data?.jobTitleId || '')}
                                    </span>

                                    {onRemove && !node.isRemovalLocked && (
                                        <button
                                            data-testid={`overflow-node-remove-${node.data?.id}`}
                                            className={
                                                'h-5 w-5 rounded-full p-0 opacity-0 transition-opacity group-hover:opacity-100'
                                            }
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onRemove(node?.data?.id || '');
                                            }}
                                        >
                                            <Icon
                                                name={'essential-close-circle'}
                                                className={'-mt-1 hover:cursor-pointer'}
                                                color={Colors.BASE}
                                                size={'sm'}
                                            />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};
